import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Descriptions, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Table, Tag, Tooltip, message,
} from 'antd';
import {
  CheckCircleOutlined, FilePdfOutlined, PlusOutlined, ReloadOutlined, SyncOutlined,
} from '@ant-design/icons';
import { testDocAPI } from '../../services/api';
import { PlanRow, PLAN_STATUS, downloadBlob } from './common';
import { gray, font } from '../../theme/tokens';

/* 테스트 계획서 탭
   승인하면 그 시점 레지스트리를 baseline 으로 동결한다 — 문서는 불변, 실행은 최신 코드.
   둘의 차이(미반영 변경)는 상세에서 경고로 드러난다. */

interface Props { bodyHeight: number; canWrite: boolean; canDelete: boolean; }

const TEXT_FIELDS: { key: keyof PlanRow; label: string; rows: number }[] = [
  { key: 'purpose', label: '목적', rows: 3 },
  { key: 'scope', label: '범위', rows: 3 },
  { key: 'out_of_scope', label: '제외 범위', rows: 2 },
  { key: 'strategy', label: '전략·방법', rows: 3 },
  { key: 'environment', label: '테스트 환경', rows: 2 },
  { key: 'schedule_desc', label: '일정', rows: 2 },
  { key: 'criteria_note', label: '합격기준 보충', rows: 2 },
  { key: 'risk_note', label: '위험·제약', rows: 2 },
];

const PlanTab: React.FC<Props> = ({ bodyHeight, canWrite, canDelete }) => {
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await testDocAPI.plans();
      setRows(res.data.items || []);
      setActiveId(res.data.active_plan_id ?? null);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '계획서를 불러오지 못했습니다.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id: number) => {
    try {
      const res = await testDocAPI.plan(id);
      setDetail(res.data);
      setEditing(false);
      form.setFieldsValue(res.data.plan);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '상세를 불러오지 못했습니다.');
    }
  };

  const doCreate = async () => {
    try {
      const v = await createForm.validateFields();
      const res = await testDocAPI.createPlan({ ...v, targets: (v.targets || []).join(',') });
      message.success(`계획서가 생성되었습니다. 케이스 ${res.data.seeded}건을 레지스트리에서 시드했습니다.`);
      setCreateOpen(false);
      createForm.resetFields();
      load();
      openDetail(res.data.plan.id);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '생성에 실패했습니다.');
    }
  };

  const doSave = async () => {
    try {
      const v = await form.validateFields();
      await testDocAPI.updatePlan(detail.plan.id, {
        ...v, targets: Array.isArray(v.targets) ? v.targets.join(',') : v.targets,
      });
      message.success('저장되었습니다.');
      setEditing(false);
      openDetail(detail.plan.id);
      load();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '저장에 실패했습니다.');
    }
  };

  const doApprove = async (id: number) => {
    try {
      await testDocAPI.approvePlan(id);
      message.success('승인되었습니다. 현재 레지스트리를 기준(baseline)으로 동결했습니다.');
      load(); openDetail(id);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '승인에 실패했습니다.');
    }
  };

  const doReseed = async (id: number) => {
    try {
      const res = await testDocAPI.reseedPlan(id);
      const d = res.data;
      message.success(`재시드 완료 — 추가 ${d.added} · 갱신 ${d.updated} · 보존 ${d.kept_custom} · 삭제 ${d.removed}.`);
      load(); openDetail(id);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '재시드에 실패했습니다.');
    }
  };

  const doPdf = async (p: PlanRow) => {
    try {
      const res = await testDocAPI.planPdf(p.id);
      downloadBlob(res, `${p.doc_no}_plan.pdf`);
    } catch {
      message.error('PDF를 만들지 못했습니다.');
    }
  };

  const columns = [
    { title: '문서번호', dataIndex: 'doc_no', width: 165,
      render: (v: string, r: PlanRow) => (
        <Space size={6}>
          <span style={{ fontFamily: font.mono, fontSize: 12, fontWeight: 600 }}>{v}</span>
          <Tag style={{ margin: 0, fontSize: 11 }}>v{r.version}</Tag>
        </Space>
      ) },
    { title: '문서명', dataIndex: 'title', ellipsis: true,
      render: (v: string, r: PlanRow) => (
        <Space size={6}>
          <span style={{ fontWeight: 600 }}>{v}</span>
          {r.id === activeId && <Tag color="green" style={{ margin: 0, fontSize: 11 }}>유효</Tag>}
        </Space>
      ) },
    { title: '대상', dataIndex: 'targets', width: 130,
      render: (v: string) => <span style={{ fontSize: 12, color: gray[500] }}>{v}</span> },
    { title: '상태', dataIndex: 'status', width: 90,
      render: (v: string) => {
        const m = PLAN_STATUS[v] || PLAN_STATUS.draft;
        return <Tag style={{ margin: 0, color: m.color, background: m.bg, borderColor: m.bg, fontWeight: 700 }}>{m.label}</Tag>;
      } },
    { title: '케이스', dataIndex: 'case_count', width: 80,
      render: (v: number) => <span style={{ fontWeight: 700 }}>{v ?? '—'}</span> },
    { title: '합격기준', width: 150,
      render: (_: any, r: PlanRow) => (
        <span style={{ fontSize: 12, color: gray[600] }}>
          통과율 {r.criteria_pass_rate}% · 커버리지 {r.criteria_coverage}%
        </span>
      ) },
    { title: '승인', width: 155,
      render: (_: any, r: PlanRow) => (r.approved_at
        ? <span style={{ fontSize: 12 }}>{r.approver} <span style={{ color: gray[400] }}>{r.approved_at}</span></span>
        : <span style={{ color: gray[300] }}>—</span>) },
    { title: '', width: 210, fixed: 'right' as const,
      render: (_: any, r: PlanRow) => (
        <Space size={4}>
          <Button size="small" onClick={() => openDetail(r.id)}>상세</Button>
          <Tooltip title="계획서 PDF">
            <Button size="small" icon={<FilePdfOutlined />} onClick={() => doPdf(r)} />
          </Tooltip>
          {canWrite && r.status === 'draft' && (
            <Popconfirm title="승인하면 현재 레지스트리가 기준으로 동결됩니다. 진행할까요?"
              okText="승인" cancelText="취소" onConfirm={() => doApprove(r.id)}>
              <Button size="small" type="primary" ghost icon={<CheckCircleOutlined />}>승인</Button>
            </Popconfirm>
          )}
          {canDelete && (
            <Popconfirm title="이 계획서와 케이스 명세를 삭제할까요?" okText="삭제" cancelText="취소"
              onConfirm={async () => {
                try { await testDocAPI.removePlan(r.id); message.success('삭제되었습니다.'); load(); }
                catch (e: any) { message.error(e?.response?.data?.detail || '삭제에 실패했습니다.'); }
              }}>
              <Button size="small" danger type="text">삭제</Button>
            </Popconfirm>
          )}
        </Space>
      ) },
  ];

  const diff = detail?.diff;

  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {canWrite && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            createForm.setFieldsValue({
              title: `SAPPT 화면테스트 계획서 ${new Date().getFullYear()}`,
              version: '1.0', targets: ['lab', 'partner'],
              criteria_pass_rate: 100, criteria_coverage: 100,
            });
            setCreateOpen(true);
          }}>계획서 작성</Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={load} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: gray[400], alignSelf: 'center' }}>
          케이스 명세는 레지스트리에서 자동 시드됩니다 · 승인 시 기준 동결
        </span>
      </div>

      <Table<PlanRow>
        rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns as any}
        scroll={{ y: bodyHeight, x: 1200 }}
        rowClassName={(_, i) => (i % 2 ? 'table-row-alt' : '')}
        pagination={{
          pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`, size: 'small',
        }}
      />

      {/* ── 계획서 상세 ── */}
      <Modal open={!!detail} onCancel={() => setDetail(null)} width={980} footer={null}
        title={detail && (
          <Space>
            <span style={{ fontFamily: font.mono }}>{detail.plan.doc_no}</span>
            <span>{detail.plan.title}</span>
            <Tag style={{ margin: 0, ...(() => { const m = PLAN_STATUS[detail.plan.status]; return { color: m.color, background: m.bg, borderColor: m.bg }; })() }}>
              {PLAN_STATUS[detail.plan.status]?.label}
            </Tag>
          </Space>
        )}>
        {detail && (
          <>
            {diff?.has_baseline && diff.total > 0 && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message={`승인 기준 이후 레지스트리 변경 ${diff.total}건이 이 계획서에 반영되지 않았습니다.`}
                description={
                  <div style={{ fontSize: 12 }}>
                    {diff.added.map((a: any) => <div key={a.key}>· 추가된 화면: <b>{a.key}</b> {a.name} ({a.path})</div>)}
                    {diff.removed.map((a: any) => <div key={a.key}>· 제거된 화면: <b>{a.key}</b> {a.name}</div>)}
                    {diff.changed.map((c: any) => (
                      <div key={c.key}>· 검사 변경: <b>{c.key}</b>
                        {c.added_checks.length ? ` 추가[${c.added_checks.join(', ')}]` : ''}
                        {c.removed_checks.length ? ` 제거[${c.removed_checks.join(', ')}]` : ''}
                      </div>
                    ))}
                    {canWrite && (
                      <Button size="small" style={{ marginTop: 8 }} icon={<SyncOutlined />}
                        onClick={() => doReseed(detail.plan.id)}>
                        케이스 명세 재시드 (손댄 문안은 보존)
                      </Button>
                    )}
                  </div>
                } />
            )}
            {!detail.plan.has_baseline && (
              <Alert type="info" showIcon style={{ marginBottom: 12 }}
                message="아직 승인되지 않아 기준이 동결되지 않았습니다. 승인하면 그 시점의 대상 화면이 이 문서의 기준이 됩니다." />
            )}

            <Space style={{ marginBottom: 12 }} wrap>
              <Button icon={<FilePdfOutlined />} onClick={() => doPdf(detail.plan)}>계획서 PDF</Button>
              <Button icon={<FilePdfOutlined />} onClick={async () => {
                try {
                  const res = await testDocAPI.casesPdf(detail.plan.id);
                  downloadBlob(res, `${detail.plan.doc_no}_cases.pdf`);
                } catch { message.error('PDF를 만들지 못했습니다.'); }
              }}>케이스 명세서 PDF</Button>
              {canWrite && detail.plan.status !== 'archived' && (
                editing
                  ? <Space><Button type="primary" onClick={doSave}>저장</Button>
                      <Button onClick={() => { setEditing(false); form.setFieldsValue(detail.plan); }}>취소</Button></Space>
                  : <Button onClick={() => setEditing(true)}>내용 편집</Button>
              )}
              {canWrite && detail.plan.status === 'draft' && (
                <Popconfirm title="승인하면 현재 레지스트리가 기준으로 동결됩니다."
                  okText="승인" cancelText="취소" onConfirm={() => doApprove(detail.plan.id)}>
                  <Button type="primary" icon={<CheckCircleOutlined />}>승인</Button>
                </Popconfirm>
              )}
            </Space>

            {editing ? (
              <Form form={form} layout="vertical" size="small">
                <Space size={12} wrap>
                  <Form.Item name="title" label="문서명" rules={[{ required: true }]} style={{ width: 320 }}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="version" label="버전" style={{ width: 90 }}><Input /></Form.Item>
                  <Form.Item name="author" label="작성" style={{ width: 120 }}><Input /></Form.Item>
                  <Form.Item name="reviewer" label="검토" style={{ width: 120 }}><Input /></Form.Item>
                  <Form.Item name="approver" label="승인" style={{ width: 120 }}><Input /></Form.Item>
                  <Form.Item name="criteria_pass_rate" label="통과율 기준(%)" style={{ width: 130 }}>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="criteria_coverage" label="커버리지 기준(%)" style={{ width: 130 }}>
                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                  </Form.Item>
                </Space>
                {TEXT_FIELDS.map(f => (
                  <Form.Item key={f.key} name={f.key} label={f.label}>
                    <Input.TextArea rows={f.rows} />
                  </Form.Item>
                ))}
              </Form>
            ) : (
              <Descriptions bordered size="small" column={2}
                labelStyle={{ width: 110, fontWeight: 600 }}>
                <Descriptions.Item label="버전">{detail.plan.version}</Descriptions.Item>
                <Descriptions.Item label="대상">{detail.plan.targets}</Descriptions.Item>
                <Descriptions.Item label="작성">{detail.plan.author || '—'}</Descriptions.Item>
                <Descriptions.Item label="검토">{detail.plan.reviewer || '—'}</Descriptions.Item>
                <Descriptions.Item label="승인">{detail.plan.approver || '—'}</Descriptions.Item>
                <Descriptions.Item label="승인일">{detail.plan.approved_at || '—'}</Descriptions.Item>
                <Descriptions.Item label="합격기준" span={2}>
                  케이스 통과율 {detail.plan.criteria_pass_rate}% 이상 · 화면 커버리지 {detail.plan.criteria_coverage}% 이상
                </Descriptions.Item>
                <Descriptions.Item label="기준 동결" span={2}>
                  {detail.plan.baseline_at
                    ? `${detail.plan.baseline_at} 시점 레지스트리 (화면 ${diff?.has_baseline ? '기준 보유' : '—'})`
                    : '미승인 — 기준 없음'}
                </Descriptions.Item>
                {TEXT_FIELDS.map(f => (
                  <Descriptions.Item key={f.key} label={f.label} span={2}>
                    <span style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{(detail.plan as any)[f.key] || '—'}</span>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </>
        )}
      </Modal>

      {/* ── 계획서 작성 ── */}
      <Modal open={createOpen} onCancel={() => setCreateOpen(false)} onOk={doCreate}
        okText="생성" cancelText="취소" width={560} title="테스트 계획서 작성">
        <Form form={createForm} layout="vertical" size="small" style={{ marginTop: 12 }}>
          <Form.Item name="title" label="문서명" rules={[{ required: true, message: '문서명을 입력하세요' }]}>
            <Input />
          </Form.Item>
          <Space size={12}>
            <Form.Item name="version" label="버전" style={{ width: 100 }}><Input /></Form.Item>
            <Form.Item name="targets" label="대상 사이트" style={{ width: 300 }}>
              <Select mode="multiple" options={[
                { value: 'lab', label: 'lab (관리자)' }, { value: 'partner', label: 'partner (파트너 포털)' }]} />
            </Form.Item>
          </Space>
          <Space size={12}>
            <Form.Item name="criteria_pass_rate" label="케이스 통과율 기준(%)" style={{ width: 190 }}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="criteria_coverage" label="화면 커버리지 기준(%)" style={{ width: 190 }}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Alert type="info" showIcon
            message="본문(목적·범위·전략·환경·일정·위험)은 표준 문안으로 채워집니다."
            description="생성 후 '내용 편집'에서 다듬을 수 있고, 케이스 명세는 레지스트리에서 자동 시드됩니다." />
        </Form>
      </Modal>
    </>
  );
};

export default PlanTab;
